/**
 * PJ モードかどうかの判定。
 *
 * 画面が PJ モードに入っているかは、**PJ 番号が読める形で入っているか**の 1 事実で決まる。
 * 判定した後の分岐は場所ごとに違う（リンクの検索条件・読取の入力・行き先ルート）が、判定
 * そのものは同じなので、ここ 1 箇所に置く。書き写すと、畳み方を変えたときに 1 箇所だけ古いまま
 * 残っても型もテストも通り、経路によって PJ が効いたり効かなかったりする。
 *
 * **空文字は「PJ なし」へ畳む。** 手打ちの URL や古いブックマークから `?pj=` だけが
 * 残った状態で来ることがあり、空の PJ 番号をそのまま読取やリンクへ渡すと、PJ を選んでいない
 * のに PJ モードの経路へ入る。`parsePjSearch` が URL を読む側で同じ畳み方をしているので、
 * 入口と出口で規則が揃う。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面が PJ モードに入っているかを判定し、作り直しの読取が受ける案件番号へ畳む。
 * @graph-connects none
 */

/**
 * PJ 番号が PJ モードとして扱える値か。
 *
 * 戻り値を型の絞り込みとして宣言してあるので、真のときは呼び出し側で `string` として扱える。
 * 判定と絞り込みが別々に書かれると、判定を通ったあとにもう一度 `undefined` を潰すことになる。
 *
 * @param pj 重ねる PJ 番号。未指定なら undefined
 * @returns PJ モードなら `true`
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business PJ 番号が空でないことをもって PJ モードと判定する。
 * @graph-connects none
 */
export function isPjMode(pj: string | undefined): pj is string {
  return pj !== undefined && pj !== '';
}

/**
 * URL の PJ 番号を、**作り直しの読取が受ける number** へ畳む。
 *
 * 画面は URL の PJ 番号を文字列で受ける（検索条件では数字だけの番号が数値で入るが、`pjTextOf` で
 * 文字列へ戻してから渡る）が、作り直しの契約は案件番号を number で受ける（決定 2）。
 * 関門を「1 以上の整数」に置くのは契約（`ElementPjNumberSchema`）と同じ線である。
 * `Number()` は空文字を 0 に、末尾の空白を無視して数に変えるので、綴りをそのまま確かめる。
 *
 * **数字にならない綴りは PJ なしへ畳む。** 今の読取は `AIRCLOSET-139162` のような綴りも
 * PJ 番号として受けるので、その形のまま作り直しの読取へ渡すと入力の検証で弾かれ、失敗が
 * 「取得に失敗しました」として画面に出る。PJ として読めない綴りは master を読む。
 *
 * @param pj URL の PJ 番号。未指定なら undefined
 * @returns 作り直しの案件番号。PJ として読めなければ undefined
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business URL の PJ 番号を作り直しの読取が受ける number へ畳み、読めない綴りは PJ なしにする。
 * @graph-connects isPjMode [calls] PJ モードかどうかの判定
 */
export function elementPjNumberOf(pj: string | undefined): number | undefined {
  if (!isPjMode(pj)) return undefined;
  if (!/^\d+$/u.test(pj)) return undefined;
  const parsed = Number(pj);
  return parsed >= 1 ? parsed : undefined;
}
